/**
 * Single source of truth for a section's subsection destinations — consumed
 * by both AppShell's bottom tab bar and each section's landing SectionMenu,
 * so the two can't drift the way Service's bottom bar and sidebar already
 * had (5 tabs vs. 7 sidebar links, not even the same 5).
 */

export type SectionKey =
  | "vehicles"
  | "sales"
  | "quotes"
  | "projects"
  | "wip"
  | "service"
  | "stock"
  | "finance"
  | "people"
  | "hs"
  | "admin"
  | "kpi";

export interface SectionLink {
  to: string;
  end?: boolean;
  icon: string;
  label: string;
  /** Permission required to show this one link. Omit for always-visible. */
  perm?: string;
  /** Set at runtime by getSectionLinks: true on the last pinned/high-priority
   * link, so a list consumer can draw a divider line after it. */
  dividerAfter?: boolean;
}

/**
 * High-priority links pinned to the top of a section, in this order; every
 * other link in the section then follows sorted A–Z by label (Lauren, 2026-09:
 * "high priority at the top, everything else A–Z, same in all sections"). The
 * pin is a section's main/landing screen, except Service which pins Contracts
 * then Service Visits explicitly. A `to` here that a viewer can't see (perm)
 * simply isn't pinned. Keyed by `to` so it can't drift from the link list.
 */
const SECTION_PRIORITY: Partial<Record<SectionKey, string[]>> = {
  service: ["/service/contracts", "/service/visits", "/service/pm"],
  vehicles: ["/vehicles/fleet"],
  sales: ["/customers"],
  quotes: ["/quotes/pipeline"],
  projects: ["/wip/jobs"],
  wip: ["/wip/jobs"],
  stock: ["/stock/dashboard"],
  finance: ["/finance/ledger"],
  people: ["/people/list"],
  hs: ["/hs/weekly"],
  admin: ["/admin/supabase-usage"],
  kpi: ["/kpi"],
};

const SECTION_LINKS: Record<SectionKey, SectionLink[]> = {
  vehicles: [
    { to: "/vehicles/fleet", end: true, icon: "▤", label: "Fleet" },
    { to: "/vehicles/list", icon: "☰", label: "All vehicles" },
    { to: "/vehicles/compliance", icon: "✓", label: "Checks & compliance" },
    { to: "/vehicles/pool-booking", icon: "▦", label: "Pool booking" },
    { to: "/vehicles/issues", icon: "⚠", label: "Issues" },
    { to: "/vehicles/report-issue", icon: "＋", label: "Report an issue" },
    { to: "/vehicles/accident", icon: "☎", label: "In case of accident" },
  ],
  /* Sales, Projects: grouping sections. They list the MODULES they
     contain rather than flattening every one of those modules' own
     subsections into one long list -- Quotes keeps its own landing with
     Pipeline/All quotes/New quote, exactly as it had before it was
     grouped. One level deeper for a quote, and a navigation that still
     reads the same way at every level. */
  sales: [
    { to: "/customers", icon: "⌂", label: "Customers" },
    { to: "/leads", icon: "◈", label: "Leads", perm: "can_view_quotes" },
    { to: "/quotes", icon: "◧", label: "Quotes", perm: "can_view_quotes" },
  ],
  /* Projects lists its destinations directly rather than a "Projects"
     tile inside a "Projects" group -- the group and the module share a
     name, so an intermediate landing would read "Home / Projects /
     Projects" and cost a tap to say nothing. Sales does not have that
     problem (Sales -> Quotes -> Pipeline reads fine), so it keeps its
     member modules and their own landings. */
  projects: [
    { to: "/wip/jobs", icon: "▤", label: "Jobs" },
    { to: "/wip/planner", icon: "▦", label: "Planner" },
    { to: "/wip/my-schedule", icon: "◧", label: "My Schedule" },
    { to: "/wip/analysis", icon: "◈", label: "Analysis" },
    { to: "/commissioning", icon: "◉", label: "Commissioning" },
  ],
  quotes: [
    { to: "/quotes/pipeline", end: true, icon: "◧", label: "Pipeline" },
    { to: "/quotes/all", icon: "▤", label: "All quotes" },
    { to: "/quotes/new", icon: "＋", label: "New quote" },
    { to: "/quotes/import", icon: "⇧", label: "Import from spreadsheet" },
    { to: "/quotes/settings", icon: "⚙", label: "Settings", perm: "can_manage_quotes" },
  ],
  service: [
    { to: "/service/dashboard", end: true, icon: "▤", label: "Dashboard" },
    { to: "/service/tickets", icon: "☰", label: "Tickets" },
    { to: "/service/visits", icon: "▦", label: "Service Visits" },
    { to: "/service/pm", icon: "▣", label: "PM Visits" },
    { to: "/service/issues", icon: "⚠", label: "Outstanding Service Issues" },
    { to: "/service/contracts", icon: "▤", label: "Contracts" },
    // Client Offices hidden from the sidebar (2026-09) — route /service/sites and
    // its page/data remain; only the nav entry is removed.
    { to: "/service/phone-log", icon: "☎", label: "Phone Log" },
    { to: "/service/renewals", icon: "↻", label: "Renewals" },
    { to: "/service/recurring-services", icon: "↻", label: "Recurring Services" },
    { to: "/service/activity-report", icon: "▤", label: "Client Report" },
  ],
  stock: [
    { to: "/stock/dashboard", end: true, icon: "▤", label: "Dashboard" },
    { to: "/stock/items", icon: "☰", label: "Items" },
    { to: "/stock/loans", icon: "▦", label: "Loans" },
    { to: "/stock/actions", icon: "↻", label: "Movement Actions" },
  ],
  finance: [
    { to: "/finance/ledger", end: true, icon: "▤", label: "Ledger" },
    { to: "/finance/import", icon: "↓", label: "Import", perm: "can_manage_finance" },
  ],
  people: [
    { to: "/people/list", end: true, icon: "☰", label: "People", perm: "can_manage_people" },
    { to: "/people/insurance", icon: "▤", label: "Insurance" },
    { to: "/people/compliance", icon: "✓", label: "Compliance", perm: "can_view_compliance" },
  ],
  wip: [
    { to: "/wip/jobs", end: true, icon: "▤", label: "Jobs" },
    { to: "/wip/planner", icon: "▦", label: "Planner" },
    { to: "/wip/my-schedule", icon: "◧", label: "My Schedule" },
    { to: "/wip/analysis", icon: "◈", label: "Analysis" },
  ],
  hs: [
    { to: "/hs/weekly", end: true, icon: "✓", label: "Weekly checks" },
    { to: "/hs/weekly/new", icon: "＋", label: "Record a check", perm: "can_manage_hs" },
    { to: "/hs/annual", icon: "↻", label: "Annual services" },
    { to: "/hs/report", icon: "▤", label: "Compliance report" },
    { to: "/hs/settings", icon: "⚙", label: "Check settings", perm: "can_manage_hs" },
  ],
  admin: [
    { to: "/admin/supabase-usage", end: true, icon: "▤", label: "Billing & usage", perm: "can_view_supabase_usage" },
    { to: "/admin/claude-usage", end: true, icon: "◈", label: "Claude API usage", perm: "can_view_ai_usage" },
    { to: "/platform/support-ticketing", icon: "☰", label: "Support ticketing" },
    { to: "/platform/integrations", icon: "⚙", label: "Integrations", perm: "can_manage_integrations" },
    { to: "/platform/system-health", icon: "♥", label: "System health" },
    // Sits under the same can_view_platform gate as the rest of this group,
    // so no extra `perm` -- but it is the one screen here holding personal
    // data about colleagues rather than about the system.
    { to: "/platform/access-log", icon: "◉", label: "Access log" },
  ],
  kpi: [
    { to: "/kpi", end: true, icon: "▤", label: "Dashboards" },
    { to: "/kpi/views", icon: "◈", label: "View Library" },
    { to: "/kpi/trusted-networks", icon: "⚙", label: "Trusted Networks", perm: "can_manage_kpi_dashboards" },
    { to: "/kpi/display-keys", icon: "⚙", label: "Display Keys", perm: "can_manage_kpi_dashboards" },
  ],
};

/** Mirrors the permission gate each section's sidebar group already uses. */
function sectionGateOk(key: SectionKey, can: (perm: string) => boolean): boolean {
  switch (key) {
    /* Sales is open to anyone who can see any of its three -- Customers
       is visible to Service and Projects people too, so gating the whole
       group on quotes would hide a door they can open. */
    case "sales":
      return can("can_view_quotes") || can("can_view_service") || can("can_view_projects");
    case "projects":
      return can("can_view_wip");
    case "quotes":
      return can("can_view_quotes");
    case "service":
      return can("can_view_service");
    case "wip":
      return can("can_view_wip");
    case "finance":
      return can("can_view_finance");
    case "stock":
      return can("can_view_stock");
    case "hs":
      return can("can_view_hs");
    case "admin":
      return can("can_view_platform");
    case "kpi":
      return can("can_view_kpi_dashboards");
    case "vehicles":
    case "people":
      return true;
  }
}

export function getSectionLinks(key: SectionKey, can: (perm: string) => boolean): SectionLink[] {
  if (!sectionGateOk(key, can)) return [];
  const visible = SECTION_LINKS[key].filter((l) => !l.perm || can(l.perm));

  // Pinned high-priority links first (in SECTION_PRIORITY order, only the ones
  // this viewer can see), then everything else A–Z by label.
  const priority = SECTION_PRIORITY[key] ?? [];
  const pinned = priority
    .map((to) => visible.find((l) => l.to === to))
    .filter((l): l is SectionLink => l != null);
  const pinnedTo = new Set(pinned.map((l) => l.to));
  const rest = visible
    .filter((l) => !pinnedTo.has(l.to))
    .sort((a, b) => a.label.localeCompare(b.label));

  const ordered = [...pinned, ...rest];
  // Flag the last pinned link so a list consumer can draw a divider after it
  // (copy, so the shared SECTION_LINKS entries are never mutated).
  if (pinned.length > 0 && rest.length > 0) {
    return ordered.map((l, i) => (i === pinned.length - 1 ? { ...l, dividerAfter: true } : l));
  }
  return ordered;
}

